import { useEffect } from "react";
import { Lock, Sparkles, X, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBilling } from "@/context/BillingContext";

const TITLE_KEY = {
  voice_not_available_in_free: "errors.voice_free_title",
  history_not_available_in_free: "errors.history_free_title",
  routines_limit: "errors.routines_limit_title",
  siri_not_available: "errors.siri_elite_title",
  insufficient_tokens: "errors.insufficient_tokens_title",
};
const BODY_KEY = {
  voice_not_available_in_free: "errors.voice_free",
  history_not_available_in_free: "errors.history_free",
  routines_limit: "errors.routines_limit",
  siri_not_available: "errors.siri_elite",
  insufficient_tokens: "errors.insufficient_tokens",
};

export default function UpgradeModal() {
  const { upgradePrompt, dismissUpgrade } = useBilling();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!upgradePrompt) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") dismissUpgrade();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [upgradePrompt, dismissUpgrade]);

  if (!upgradePrompt) return null;
  const titleKey = TITLE_KEY[upgradePrompt.error] || "errors.upgrade_cta";
  const bodyKey = BODY_KEY[upgradePrompt.error];
  const tier = upgradePrompt.requireTier;

  const handleSee = () => {
    dismissUpgrade();
    navigate("/billing");
  };

  return (
    <div
      data-testid="upgrade-modal-overlay"
      className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/65 backdrop-blur-sm animate-fade-in-up px-4"
      role="dialog"
      aria-label={t(titleKey)}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) dismissUpgrade();
      }}
    >
      <div
        data-testid="upgrade-modal"
        className="
          relative w-full max-w-[360px] rounded-[24px] px-6 pt-6 pb-5
          bg-[rgba(10,14,26,0.96)] backdrop-blur-xl
          border border-[rgba(0,229,255,0.4)]
          shadow-[0_30px_80px_-10px_rgba(0,0,0,0.75),0_0_60px_-12px_rgba(0,229,255,0.45)]
        "
      >
        <button
          type="button"
          onClick={dismissUpgrade}
          aria-label={t("common.close")}
          data-testid="upgrade-modal-close"
          className="absolute top-3 right-3 h-8 w-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-white/[0.06] hover:text-[#E5E7EB] transition-colors"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>

        <div
          className="h-12 w-12 rounded-full mx-auto mb-3 bg-[rgba(0,229,255,0.1)] border border-[rgba(0,229,255,0.5)] flex items-center justify-center shadow-[0_0_24px_rgba(0,229,255,0.45)]"
          aria-hidden="true"
        >
          {upgradePrompt.error === "siri_not_available" ? (
            <Lock className="h-5 w-5 text-[#00E5FF]" strokeWidth={1.75} />
          ) : (
            <Sparkles className="h-5 w-5 text-[#00E5FF]" strokeWidth={1.75} />
          )}
        </div>

        <p
          data-testid="upgrade-modal-tier"
          className="text-[10.5px] uppercase tracking-[0.24em] text-[#00E5FF]/85 text-center"
        >
          {tier}
        </p>
        <h3
          className="mt-1 text-[18px] font-semibold text-[#E5E7EB] text-center leading-tight"
          data-testid="upgrade-modal-title"
        >
          {t(titleKey)}
        </h3>
        {bodyKey ? (
          <p className="mt-2 text-[12.5px] text-[#9CA3AF] text-center leading-relaxed">
            {t(bodyKey)}
          </p>
        ) : null}
        {upgradePrompt.error === "insufficient_tokens" ? (
          <p className="mt-1 text-[11.5px] text-[#9CA3AF] text-center">
            Saldo: <span className="font-mono text-[#E5E7EB]">{upgradePrompt.balance ?? 0}</span>
          </p>
        ) : null}

        <div className="mt-5 flex items-center gap-2">
          <button
            type="button"
            onClick={dismissUpgrade}
            data-testid="upgrade-modal-stay"
            className="flex-1 h-10 rounded-full border border-white/[0.1] text-[#E5E7EB] text-[12.5px] hover:bg-white/[0.04]"
          >
            {t("errors.stay_free")}
          </button>
          <button
            type="button"
            onClick={handleSee}
            data-testid="upgrade-modal-cta"
            className="
              flex-1 h-10 rounded-full
              bg-[rgba(0,229,255,0.18)] border border-[rgba(0,229,255,0.55)]
              text-[#00E5FF] font-semibold text-[12.5px]
              hover:bg-[rgba(0,229,255,0.28)]
              shadow-[0_0_20px_rgba(0,229,255,0.35)]
              inline-flex items-center justify-center gap-1.5
            "
          >
            {t("errors.upgrade_cta")}
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );
}
